import Mode from "@zxing/library/esm/core/qrcode/decoder/Mode";
import { BehaviorSubject, Subject } from "rxjs";

export enum TransportOptionConstraintType {
  MIN_SELECTED_COUNT = 'MIN_SELECTED_COUNT',
  MAX_SELECTED_COUNT = 'MAX_SELECTED_COUNT',
}

export enum TransportOptionsPanelModelUsage {
  TIME = 'TIME',
  TERMINAL = 'TERMINAL',
  OTHER = 'OTHER'
}

export class TransportOptionsPanelModel {

  readOnly: boolean = false;
  selectionChange = new Subject<void>();
  valid: boolean = false;
  dirty: boolean = false;
  validState = new BehaviorSubject<boolean>(false);
  usage: TransportOptionsPanelModelUsage;

  optionStatus = new Map();

  messages = new Array<string>();
  columns = new Array<TransportOptionColumn>();
  constraints = new Array<TransportOptionConstraint>();
  selection = new Array<TransportOptionSelection>();

  constructor(model?: Partial<TransportOptionsPanelModel>) {
    if (model != null) {
      if (model.columns != null) {
        for (const column of model.columns) {
          this.columns.push(new TransportOptionColumn(column));
        }

        for (const column of this.columns) {
          column.options = column.options.sort((a, b) => {
            return (a.rowIndex - b.rowIndex);
          });
        }
      }
      if (model.constraints != null) {
        for (const constraint of model.constraints) {
          this.constraints.push(new TransportOptionConstraint(constraint));
        }
      }
      if (model.selection != null) {
        for (const selection of model.selection) {
          this.selection.push(new TransportOptionSelection(selection));
        }
      }
      this.readOnly = model.readOnly;
      this.usage = model.usage != null ? model.usage : TransportOptionsPanelModelUsage.OTHER;
    }
    this.validate();
  }

  validate() {

    let messages = new Array<string>();
    this.optionStatus.clear();

    let constraintFailed = false;
    if (!this.checkRequiredFieldsAreValid()) {
      constraintFailed = true;
      messages.push("tmc-yard.entity.transport-option-constraint.messages.required-fields-missing");
    }

    if (!this.checkRequiredLoadingIsValid()) {
      constraintFailed = true;
      messages.push("tmc-yard.entity.transport-option-constraint.messages.required-loading-amount-missing");
    }

    for (const constraint of this.constraints) {

      const selectedOptionsCount = this.countSelectedOptions(constraint.transportOptionIds);
      if (TransportOptionConstraintType.MIN_SELECTED_COUNT === constraint.type) {
        if (selectedOptionsCount < constraint.count) {
          constraintFailed = true;
          messages.push("tmc-yard.entity.transport-option-constraint.messages.constraint-failed");
        }
      } else if (TransportOptionConstraintType.MAX_SELECTED_COUNT === constraint.type) {
        if (selectedOptionsCount > constraint.count) {
          constraintFailed = true;
          messages.push("tmc-yard.entity.transport-option-constraint.messages.constraint-failed");
        }

      }

    }

    this.valid = !constraintFailed;
    this.messages = messages;
    this.validState.next(this.valid);

  }

  public getOnlySelectedTransportOptionSelection(): Array<TransportOptionSelection> {
    const selection = new Array<TransportOptionSelection>();

    for (const column of this.columns) {
      for (const option of column.options) {
        const optionSelection = this.findSelection(option.id);
        if (optionSelection?.selected === true) {
          selection.push(optionSelection);
          for (const childOption of option.options) {
            const childSelection = this.findSelection(childOption.id);
            if (childSelection?.selected === true) {
              selection.push(childSelection);
            }
          }
        }
      }
    }

    return selection;
  }

  checkRequiredFieldsAreValid(): boolean {
    let result = true;
    for (const column of this.columns) {
      for (const option of column.options) {
        if (this.checkOptionIsSelected(option.id)) {
          for (const subOption of option.options) {
            if (!subOption.optional) {
              if (!this.checkOptionIsSelected(subOption.id)) {
                result = false;
                // TODO add message here
                this.optionStatus.set(subOption.id, 'required');
              }
            }
          }
          if (option.options.length > 0) {
            const selectedChildCount = this.countSelectedOptions(option.options.map(({ id }) => id));
            if (selectedChildCount < option.minimumSelectedOptions) {
              result = false;
              // TODO add message here
              for (const subOption of option.options) {
                this.optionStatus.set(subOption.id, 'required');
              }
            }
          }
        } else {
          if (!option.optional) {
            result = false;
            // TODO add message here
            this.optionStatus.set(option.id, 'required');
          }
        }
      }
    }
    return result;
  }

  checkOptionIsSelected(id: number): boolean {
    for (const selection of this.selection) {
      if (selection.transportOptionId === id) {
        if (selection.selected === true) {
          return true;
        }
      }
    }
    return false;
  }

  countSelectedOptions(transportOptionIds: number[]): number {
    let result = 0;
    for (const number of transportOptionIds) {
      for (const selection of this.selection) {
        if (selection.transportOptionId === number) {
          if (selection.selected === true) {
            result += 1;
          }
        }
      }
    }
    return result;
  }

  private checkRequiredLoadingIsValid(): boolean {
    for (const column of this.columns) {
      for (const option of column.options) {
        if (this.checkOptionIsSelected(option.id)) {
          if (!this.checkAmountInputIsValid(option)) {
            return false;
          }
          for (const subOption of option.options) {
            if (this.checkOptionIsSelected(subOption.id) && !this.checkAmountInputIsValid(subOption)) {
              return false;
            }
          }
        }
      }
    }
    return true;
  }

  private checkAmountInputIsValid(option: TransportOption) {

    console.log('validating option', this, option);

    if (this.usage === TransportOptionsPanelModelUsage.OTHER ||
      (this.usage === TransportOptionsPanelModelUsage.TERMINAL && !option.amountMandatoryInTerminal) ||
      (this.usage === TransportOptionsPanelModelUsage.TIME && !option.amountMandatoryInTime)) {
      return true;
    }

    console.log('validation required');

    const optionSelection = this.findSelection(option.id);
    if (optionSelection != null && optionSelection.selected) {
      if (option.amountInKilogrammMandatory && optionSelection.amountInKilogramm <= 0) {
        return false;
      }
      if (option.amountInLiterMandatory && optionSelection.amountInLiter <= 0) {
        return false;
      }
      if (option.amountOthersMandatory && (optionSelection.amountOthers == null || optionSelection.amountOthers.trim().length == 0)) {
        return false;
      }
      if (option.amountOfPallettesMandatory && optionSelection.amountOfPallettes <= 0) {
        return false;
      }
      if (option.amountOfBoxesMandatory && optionSelection.amountOfBoxes <= 0) {
        return false;
      }
    }

    return true;
  }

  private findSelection(optionId: number): TransportOptionSelection {
    return this.selection.find(s => s.transportOptionId === optionId);
  }

  notifySelectionChange() {
    this.selectionChange.next();
  }
}

export class TransportOptionColumn {

  options = new Array<TransportOption>();

  constructor(column?: any) {
    if (column != null) {
      if (column.options != null) {
        for (const option of column.options) {
          this.options.push(new TransportOption(option));
        }
      }
    }
  }

}

export class TransportOption {

  id: number;
  externalKey: string;
  nameDictionaryKey: string;
  hintDriverDictionaryKey: string;
  allwaysShowHint: boolean;
  optional: boolean;
  minimumSelectedOptions: number;
  columnIndex: number;
  rowIndex: number;
  onlyInternal: boolean;
  color: string;
  messagingReference: string;
  amountOfPallettesMandatory: boolean;
  amountOfBoxesMandatory: boolean;
  amountInKilogrammMandatory: boolean;
  amountInLiterMandatory: boolean;
  amountOthersMandatory: boolean;

  amountMandatoryInTerminal: boolean;
  amountMandatoryInTime: boolean;

  readOnlyAtTerminal: boolean;

  parameter = new Array<TransportOptionParameter>();

  options = new Array<TransportOption>();

  constructor(option?: any) {
    if (option != null) {

      this.id = option.id;
      this.externalKey = option.externalKey;
      this.nameDictionaryKey = option.nameDictionaryKey;
      this.hintDriverDictionaryKey = option.hintDriverDictionaryKey;
      this.optional = option.optional;
      this.minimumSelectedOptions = option.minimumSelectedOptions;
      this.columnIndex = option.columnIndex;
      this.rowIndex = option.rowIndex;
      this.onlyInternal = option.onlyInternal;
      this.color = option.color;
      this.messagingReference = option.messagingReference;
      this.amountOfPallettesMandatory = option.amountOfPallettesMandatory;
      this.amountOfBoxesMandatory = option.amountOfBoxesMandatory;
      this.amountInKilogrammMandatory = option.amountInKilogrammMandatory;
      this.amountInLiterMandatory = option.amountInLiterMandatory;
      this.amountOthersMandatory = option.amountOthersMandatory;

      this.amountMandatoryInTerminal = option.amountMandatoryInTerminal;
      this.amountMandatoryInTime = option.amountMandatoryInTime;

      this.allwaysShowHint = option.allwaysShowHint;

      this.readOnlyAtTerminal = option.readOnlyAtTerminal;

      if (option.options != null) {
        for (const subOption of option.options) {
          this.options.push(new TransportOption(subOption));
        }
      }

      if (option.parameter != null) {
        for (const parameter of option.parameter) {
          this.parameter.push(new TransportOptionParameter(parameter));
        }
      }

      // temp workaround da im alten Model noch anders benannt
      if (option.transportOptions != null) {
        for (const subOption of option.transportOptions) {
          this.options.push(new TransportOption(subOption));
        }
      }

    }
  }

}

export class TransportOptionParameter {

  id: string;
  type: string;
  parameterValues = new Array<TransportOptionParameterValue>();

  constructor(parameter?: any) {
    if (parameter != null) {
      this.id = parameter.id;
      this.type = parameter.type;
      if (parameter.values != null) {
        for (const value of parameter.values) {
          this.parameterValues.push(new TransportOptionParameterValue(value));
        }
      }
    }
  }

}

export class TransportOptionSelection {

  selected = false;
  fixed = false;

  transportOptionId: number;

  amountOfPallettes: number;
  amountOfBoxes: number;
  amountInKilogramm: number;
  amountInLiter: number;
  amountOthers: string;

  parameterValues = new Array<TransportOptionParameterValue>();

  constructor(selection?: any) {
    if (selection != null) {
      this.amountOfPallettes = selection.amountOfPallettes;
      this.amountOfBoxes = selection.amountOfBoxes;
      this.amountInKilogramm = selection.amountInKilogramm;
      this.amountInLiter = selection.amountInLiter;
      this.amountOthers = selection.amountOthers;
      this.transportOptionId = selection.transportOptionId;
      if (selection.parameterValues != null) {
        for (const value of selection.parameterValues) {
          this.parameterValues.push(new TransportOptionParameterValue(value));
        }
      }
      this.selected = selection.selected;
      this.fixed = selection.fixed;
    }
  }

}

export class TransportOptionConstraint {

  id: number;
  name: string;
  count: number;
  type = TransportOptionConstraintType.MIN_SELECTED_COUNT;
  transportOptionIds = new Array<number>();

  constructor(constraint?: any) {
    if (constraint != null) {
      this.id = constraint.id;
      this.name = constraint.name;
      this.count = constraint.count;
      this.type = constraint.type;
      if (constraint.transportOptionIds != null) {
        this.transportOptionIds.push(...constraint.transportOptionIds);
      }
    }
  }

}

export class TransportOptionParameterValue {

  id: number;
  valueDictionaryKey: string;

  constructor(value?: any) {
    if (value != null) {
      this.id = value.id;
      this.valueDictionaryKey = value.valueDictionaryKey;
    }
  }

}
